import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../supabase/supabase';

export default function Rewards() {
  const { user, badges, userBadges, activeWinner, addNotification, setActiveWinner } = useApp();
  const [activeTab, setActiveTab] = useState('badges');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    }
  }, [activeTab]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('profiles_v3')
      .select('id, first_name, email, xp, monthly_xp, level')
      .order('monthly_xp', { ascending: false })
      .limit(10);
    if (data) setLeaderboard(data);
    setLoading(false);
  };

  const getNextLevelXp = (xp) => {
    if (xp < 200) return 200;
    if (xp < 500) return 500;
    if (xp < 1000) return 1000;
    return xp;
  };

  const claimReward = async (choice) => {
    const { error } = await supabase
      .from('monthly_winners_v1')
      .update({ reward_choice: choice, is_claimed: true })
      .eq('id', activeWinner.id);

    if (!error) {
      addNotification(`Great choice! Your ${choice} card will be sent to ${user.email}`, 'success');
      setActiveWinner(null);
    }
  };

  const formatLeaderboardName = (entry) => {
    if (entry.id === user.id) return "You (Champion)";
    const name = entry.first_name || entry.email.split('@')[0];
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0]} ${parts[1][0]}.`;
    }
    return `${name[0]}***${name.slice(-1)}`;
  };

  const nextTierXp = getNextLevelXp(user.xp);
  const progressPercent = Math.min(100, (user.xp / nextTierXp) * 100);

  return (
    <div className="p-4 pb-24 md:pb-8 space-y-6 max-w-lg mx-auto">
      {/* Winner Notification */}
      <AnimatePresence>
        {activeWinner && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }}
            className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 rounded-3xl shadow-2xl overflow-hidden text-slate-900 mb-6"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="bg-white/20 p-3 rounded-2xl">
                <SafeIcon icon={FiIcons.FiGift} className="text-3xl" />
              </div>
              <div>
                <h3 className="text-xl font-black uppercase tracking-tight">You Won!</h3>
                <p className="text-sm font-bold opacity-90">Monthly Top Contributor</p>
              </div>
            </div>
            <p className="text-sm mb-6 font-medium">Select your $10 Gift Card reward:</p>
            <div className="grid grid-cols-3 gap-2">
              {['Starbucks', 'Amazon', 'Target'].map(card => (
                <button 
                  key={card}
                  onClick={() => claimReward(card)}
                  className="bg-white/20 hover:bg-white/40 py-3 rounded-xl text-[10px] font-black uppercase transition-all border border-white/10"
                >
                  {card}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="text-center space-y-2 pt-4">
        <div className="inline-block p-4 rounded-3xl bg-amber-500/10 border border-amber-500/20 mb-2 rotate-3 shadow-xl">
          <SafeIcon icon={FiIcons.FiAward} className="text-5xl text-amber-500" />
        </div>
        <h2 className="text-3xl font-black text-white tracking-tight">Traveler Rewards</h2>
        <div className="flex items-center justify-center space-x-2">
          <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-1 rounded font-bold uppercase tracking-widest">
            Level {user.level} Member
          </span>
        </div>
      </div>

      {/* XP Progress Card */}
      <div className="bg-slate-850 p-6 rounded-3xl border border-slate-800 shadow-2xl relative overflow-hidden">
        <div className="flex justify-between items-end mb-3">
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Experience Points</p>
            <p className="text-3xl font-mono font-bold text-white">{user.xp}</p>
          </div>
          <span className="text-xs font-mono text-amber-500 font-bold">
            Target: {nextTierXp} XP
          </span>
        </div>
        <div className="w-full h-4 bg-slate-900 rounded-full overflow-hidden border border-slate-800 p-0.5">
          <motion.div 
            initial={{ width: 0 }} 
            animate={{ width: `${progressPercent}%` }} 
            className="h-full bg-gradient-to-r from-amber-600 to-amber-400 rounded-full shadow-[0_0_15px_rgba(245,158,11,0.3)]"
          />
        </div>
        <p className="text-[10px] text-slate-500 text-center mt-3 font-bold uppercase tracking-wider">
          {nextTierXp - user.xp > 0 ? `${nextTierXp - user.xp} XP until your next promotion` : 'Highest Rank Achieved!'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-900 p-1 rounded-2xl border border-slate-800">
        {['badges', 'leaderboard', 'quests'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === tab ? 'bg-amber-500 text-slate-900 shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'badges' && (
          <motion.div 
            key="badges"
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-2 gap-4"
          >
            {badges.map(badge => {
              const isEarned = userBadges.some(ub => ub.name === badge.name);
              return (
                <div 
                  key={badge.id} 
                  className={`p-5 rounded-3xl border flex flex-col items-center text-center transition-all relative overflow-hidden ${isEarned ? 'bg-slate-850 border-amber-500/30 shadow-xl' : 'bg-slate-900/50 border-slate-800 opacity-40'}`}
                >
                  {isEarned && <div className="absolute top-0 right-0 p-2"><SafeIcon icon={FiIcons.FiCheckCircle} className="text-amber-500 text-xs" /></div>}
                  <div className={`p-3 rounded-2xl mb-3 ${isEarned ? 'bg-amber-500/10 text-amber-500' : 'bg-slate-800 text-slate-600'}`}>
                    <SafeIcon icon={FiIcons[badge.icon_name] || FiIcons.FiAward} className="text-3xl" />
                  </div>
                  <span className="font-bold text-sm text-white mb-1 leading-tight">{badge.name}</span>
                  <span className="text-[10px] text-slate-500 leading-tight">{badge.description}</span>
                  <div className="mt-3 text-[9px] font-black text-amber-500 bg-amber-500/5 px-2 py-1 rounded-full border border-amber-500/10">
                    +{badge.xp_reward} XP
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {activeTab === 'leaderboard' && (
          <motion.div 
            key="leaderboard"
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="bg-slate-850 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
              <div className="p-4 bg-slate-900/50 border-b border-slate-800 flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiIcons.FiUsers} className="text-amber-500" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monthly Leaderboard</span>
                </div>
                <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded font-bold">
                  {leaderboard.length < 3 ? `Need ${3 - leaderboard.length} more players` : 'Prize Unlocked'}
                </span>
              </div>

              <div className="divide-y divide-slate-800/50">
                {loading ? (
                  <div className="p-12 text-center text-slate-500 text-xs">Syncing results...</div>
                ) : (
                  leaderboard.map((entry, idx) => (
                    <div 
                      key={entry.id} 
                      className={`p-4 flex items-center justify-between transition-colors ${entry.id === user.id ? 'bg-amber-500/5' : ''}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs ${
                          idx === 0 ? 'bg-amber-500 text-slate-900' : 
                          idx === 1 ? 'bg-slate-300 text-slate-900' : 
                          idx === 2 ? 'bg-orange-400 text-slate-900' : 'bg-slate-800 text-slate-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className={`text-sm font-bold ${entry.id === user.id ? 'text-amber-400' : 'text-white'}`}>
                            {formatLeaderboardName(entry)}
                          </p>
                          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter">{entry.level} Tier</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono font-bold text-white">{entry.monthly_xp}</p>
                        <p className="text-[8px] text-slate-600 uppercase font-black">Monthly XP</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Rules Card */}
            <div className="bg-slate-900/50 p-6 rounded-3xl border border-slate-800 border-dashed">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center">
                <SafeIcon icon={FiIcons.FiShield} className="mr-2 text-blue-500" /> Integrity Rules
              </h4>
              <ul className="space-y-3">
                {[
                  "XP cooldown: 1 reward every 4 hours",
                  "Must be within 10 miles of SMF to report",
                  "Minimum 3 participants required for monthly prize",
                  "Winner announced on the 1st of every month"
                ].map((rule, i) => (
                  <li key={i} className="flex items-start space-x-3 text-[11px] text-slate-500 font-medium">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-emerald-500 mt-0.5" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}

        {activeTab === 'quests' && (
          <motion.div 
            key="quests"
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {[
              { title: 'Morning Sentinel', desc: 'Report a wait time before 8:00 AM', reward: 30 },
              { title: 'Terminal Hopper', desc: 'Report from both Terminals in 24h', reward: 100 },
              { title: 'Weekend Warrior', desc: 'Report on a Saturday or Sunday', reward: 40 },
              { title: 'Parking Scout', desc: 'Verify a parking lot status', reward: 15 }
            ].map((quest, i) => (
              <div key={i} className="bg-slate-850 p-5 rounded-2xl border border-slate-800 flex items-center justify-between group hover:border-amber-500/30 transition-all">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400">
                    <SafeIcon icon={FiIcons.FiTarget} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">{quest.title}</h4>
                    <p className="text-[10px] text-slate-500">{quest.desc}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-black text-emerald-400">+{quest.reward} XP</span>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}